import {
  BubbleAccordion,
  BubbleAlert,
  BubbleButton,
  BubbleDebounce,
  BubbleFormGroup,
  BubbleInput,
  BubbleOverlay,
  BubbleTable,
} from '@go-client/components/BubbleUI';

import { MenuBar } from '@go-client/components';
import { BrandLogo } from '@go-client/components/UIkit/Brand/BrandLogo/BrandLogo';
import { PropertyBanner } from '@go-client/components/UIkit/Property/PropertyBanner';
import React, { useEffect, useMemo, useState } from 'react';
import { RouteComponentProps } from 'react-router';

import {
  BubbleTableFieldBehavior,
  BubbleTableFields,
  BubbleTableTemplate,
} from '@go-client/components/BubbleUI/BubbleTable';
import TemplateGlanceButton from '@go-client/components/TemplateGlanceButton';
import { useActiveProperty } from '@go-client/context/ActivePropertyContext';
import {
  createSailing,
  deleteSailing,
  getSailing,
  getSimpleSailingsByProperty,
  updateSailing,
} from '@go-client/lib/api/itinerary';
import Page from '@go-client/pages/Page';
import PageWrapper from '@go-client/pages/PageWrapper';
import { firstEl, lastEl, mapBy, move, notUndefined, swap } from '@go-shared/lib/functional';
import { getDayFirstArrival, getDayLastDeparture, getDayLocation } from '@go-shared/lib/sailing';
import {
  addDaysToDateUTC,
  DEFAULT_TIMESTR_FORMAT,
  getLocaleDateStringUTC,
  dateToIsoUTC,
  isoLocal,
} from '@go-shared/lib/time';
import { hashStringToColor } from '@go-shared/lib/utils';
import {
  BareSailing,
  BareSailingDay,
  BareSimpleSailing,
  BareSimpleSailingDayBundle,
  NewBareSailingDay,
} from '@go-shared/types/sailing';
import { Alert, Popup } from '@spark-labs/avocado-ui';
import ItineraryNavigation from '@go-client/components/Navigations/ItineraryNavigation';
import { resolveFeedPath, resolveItinerariesPath, resolvePlannerPath } from '@go-client/routes';
import { useUserInfo } from '@go-client/context/UserInfoContext';
import { getHourFormat } from '@go-shared/lib/property';
import { unsimplifyBareSailing } from '@go-shared/lib/itinerary';
import { DayMarkersButton } from '@go-client/components/DayMarkersButton';
import { canEditSailingInfo } from '@go-shared/lib/permissions';
import { EditDayForm, EditDaySidePanel } from '@go-client/components/EditDayForm';
import { PropertyType } from '@go-shared/types/property';

import './ItineraryManagement.scss';

interface ItineraryManagementRouteParams {
  property: string;
}

type ItineraryManagementSailingBundle = BareSimpleSailingDayBundle & {
  sailingIndex: number;
  uniqueComboId: string;
};

interface ItineraryManagementTableItem {
  id: string;
  date: string;
  dayOfTrip: string;
  sailing: string;
  location: string;
  arrival: string;
  departure: string;
  totalNumOfDays: number;
  isFirstDay: boolean;
  isSecondDay: boolean;
  isLastDay: boolean;
  isCurrentDay: boolean;
  _data: ItineraryManagementSailingBundle;
}

interface BulkItineraryManagementTableItem {
  sailing: BareSimpleSailing;
  items: ItineraryManagementTableItem[];
}

interface EditSailingForm {
  name: string;
  _dirty: boolean;
}

function generateSailingEditForm(sailing?: BareSimpleSailing): EditSailingForm {
  return { name: sailing?.name ?? '', _dirty: false };
}

const DEFAULT_SAILING_DATE = isoLocal();

interface SpotCreateForm {
  date: string;
  numOfDays: number;
  repeatEvery: number;
  repeatFor: number;
  locations: string;
  useDateSailingNames: boolean;
}

function generateSpotCreateForm(date: string, type: PropertyType): SpotCreateForm {
  return {
    date,
    numOfDays: type === 'sea' ? 5 : 7,
    repeatEvery: type === 'sea' ? 4 : 7,
    repeatFor: 1,
    locations: type === 'sea' ? 'Port A,,Port B,,Port A' : '',
    useDateSailingNames: true,
  };
}

const ItineraryManagement: React.FC<
  RouteComponentProps<ItineraryManagementRouteParams>
> = props => {
  const [bulkTableItems, setBulkTableItems] = useState<BulkItineraryManagementTableItem[]>([]);
  const [simpleSailings, setSimpleSailings] = useState<BareSimpleSailing[]>([]);
  const [currentDate, setCurrentDate] = useState(DEFAULT_SAILING_DATE);
  const [loadingItineraries, setLoadingItineraries] = useState(false);
  const [spotCreateForm, setSpotCreateForm] = useState<SpotCreateForm | undefined>(undefined);

  const [sailingToEdit, setSailingToEdit] = useState<BareSimpleSailing | undefined>(undefined);
  const [dayToEdit, setDayToEdit] = useState<EditDaySidePanel | undefined>(undefined);

  const { activeProperty } = useActiveProperty();
  const { user } = useUserInfo();
  const hourFormat = getHourFormat(activeProperty);

  useEffect(() => {
    fetchItineraries().catch(console.error);
  }, [activeProperty, currentDate]);

  async function fetchItineraries() {
    setLoadingItineraries(true);
    setDayToEdit(undefined);

    try {
      setLoadingItineraries(true);
      const simplSails = await getSimpleSailingsByProperty(activeProperty._id, currentDate);
      setSailingToEdit(simplSails.find(s => s._id === sailingToEdit?._id));
      setSimpleSailings(simplSails);
    } finally {
      setLoadingItineraries(false);
    }
  }

  const simplSailBndlBySail: ItineraryManagementSailingBundle[][] = useMemo(
    () =>
      simpleSailings.map((s, i) =>
        s.days.map((d, j) => ({
          dateIso: dateToIsoUTC(d.date),
          dayIndex: j,
          sailing: s,
          day: d,
          sailingIndex: i,
          uniqueComboId: `${s._id}-${d._id}`,
        }))
      ),
    [simpleSailings]
  );

  useEffect(() => {
    const tItems: BulkItineraryManagementTableItem[] = simplSailBndlBySail.map(sbb => ({
      sailing: sbb[0].sailing,
      _expanded: sbb[0].sailing._id === sailingToEdit?._id,
      items: sbb.map(sb => {
        let firstArrival = getDayFirstArrival(sb.day, hourFormat, DEFAULT_TIMESTR_FORMAT);
        let lastDeparture = getDayLastDeparture(sb.day, hourFormat, DEFAULT_TIMESTR_FORMAT);
        const isCurrentDay = currentDate === dateToIsoUTC(sb.day.date);

        return {
          id: sb.day._id,
          date: getLocaleDateStringUTC(sb.day.date),
          dayOfTrip: sb.day.name,
          sailing: sb.sailing.name,
          location: getDayLocation(sb.day),
          arrival: firstArrival,
          departure: lastDeparture,
          totalNumOfDays: sb.sailing.days.length,
          isFirstDay: sb.dayIndex === 0,
          isSecondDay: sb.dayIndex === 1,
          isLastDay: sb.dayIndex === sb.sailing.days.length - 1,
          isCurrentDay,
          _data: sb,
        };
      }),
    }));

    setBulkTableItems(tItems);
  }, [simplSailBndlBySail, sailingToEdit]);

  const tableFields: Array<BubbleTableFields<ItineraryManagementTableItem>> = [
    {
      key: 'organize',
      behavior: BubbleTableFieldBehavior.Condense,
    },
    { key: 'date' },
    { key: 'dayOfTrip', label: 'Day' },
    { key: 'location' },
    { key: 'arrival' },
    { key: 'departure' },
    {
      key: 'template',
      behavior: BubbleTableFieldBehavior.Condense,
    },
    {
      key: 'markers',
      behavior: BubbleTableFieldBehavior.Condense,
    },
    {
      key: 'actions',
      label: '',
      behavior: BubbleTableFieldBehavior.Condense,
    },
  ];

  const tableTemplates: Array<BubbleTableTemplate<ItineraryManagementTableItem>> = [
    {
      key: 'organize',
      render: item => (
        <div className="-ml-3">
          {item.isFirstDay || item.isLastDay ? (
            <BubbleButton
              variant="navy"
              inverse
              className="p-2-i"
              disabled={!isJoinEnabled(item._data, item.isFirstDay ? -1 : 1)}
              onClick={() => handleSimplSailJoinSail(item._data, item.isFirstDay ? -1 : 1)}
              icon="merge"
              iconRotate={item.isFirstDay ? 270 : 90}
            />
          ) : (
            <BubbleButton
              variant="navy"
              inverse
              className="p-2-i"
              onClick={() => handleSimplSailSplitSail(item._data)}
              icon="split"
            />
          )}
          {item.isFirstDay || item.isLastDay ? (
            <>
              <BubbleButton
                variant={!isJoinEnabled(item._data, item.isFirstDay ? -1 : 1) ? 'lime' : 'banana'}
                inverse
                className="p-2-i"
                disabled={!isPlusEnabled(item._data, item.isFirstDay ? -1 : 1)}
                onClick={() =>
                  handlePlusDay(item._data, item.isFirstDay && !item.isLastDay ? -1 : 1)
                }
                icon={
                  !isJoinEnabled(item._data, item.isFirstDay ? -1 : 1)
                    ? 'plus'
                    : item.isFirstDay
                      ? 'arrow-down'
                      : 'arrow-up'
                }
              />
              <BubbleButton
                variant={
                  !isJoinEnabled(item._data, item.isFirstDay ? -1 : 1) ? 'crimson' : 'banana'
                }
                inverse
                className="p-2-i"
                disabled={!isMinusEnabled(item._data, item.isFirstDay ? -1 : 1)}
                onClick={() => handleMinusDay(item._data, item.isFirstDay ? -1 : 1)}
                icon={
                  !isJoinEnabled(item._data, item.isFirstDay ? -1 : 1)
                    ? 'minus'
                    : item.isFirstDay
                      ? 'arrow-up'
                      : 'arrow-down'
                }
              />
              <BubbleButton
                variant="crimson"
                inverse
                className="p-2-i"
                disabled={!item.isLastDay || item.isFirstDay}
                onClick={() => handleCrossDay(item._data)}
                icon="trash-alt"
              />
            </>
          ) : (
            <>
              <BubbleButton
                variant="slate"
                inverse
                className="p-2-i"
                onClick={() => handleSimplSailMoveWithinSail(item._data, -1)}
                icon="arrow-up"
              />
              <BubbleButton
                variant="slate"
                inverse
                className="p-2-i"
                onClick={() => handleSimplSailMoveWithinSail(item._data, 1)}
                icon="arrow-down"
              />
              <BubbleButton
                variant="crimson"
                inverse
                className="p-2-i"
                disabled={!item.isLastDay || item.isFirstDay}
                onClick={() => handleCrossDay(item._data)}
                icon="trash-alt"
              />
            </>
          )}
        </div>
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
    { key: 'date', tdProps: makeDefaultTemplateTdProps },
    { key: 'dayOfTrip', tdProps: makeDefaultTemplateTdProps },
    { key: 'location', tdProps: makeDefaultTemplateTdProps },
    { key: 'arrival', tdProps: makeDefaultTemplateTdProps },
    { key: 'departure', tdProps: makeDefaultTemplateTdProps },
    {
      key: 'template',
      render: item => (
        <div className="mt-2">
          <TemplateGlanceButton
            disabled
            locked={item._data.day.locked}
            templateHistory={item._data.day.templateHistory}
            templateFragments={item._data.day.templateFragments}
          />
        </div>
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'markers',
      render: item => <DayMarkersButton sailingDay={item._data.day} />,
      tdProps: makeDefaultTemplateTdProps,
    },
    {
      key: 'actions',
      render: item => (
        <>
          <BubbleButton
            className="p-2-i"
            variant="navy"
            inverse
            linkProps={{
              to: resolvePlannerPath(
                item._data.sailing.property,
                item._data.sailing._id,
                item._data.day._id
              ),
              target: '_blank',
            }}
            icon={['fass', 'chart-tree-map']}
          />
          <BubbleButton
            className="p-2-i"
            variant="navy"
            inverse
            linkProps={{
              to: resolveFeedPath(
                item._data.sailing.property,
                item._data.sailing._id,
                item._data.day._id
              ),
              target: '_blank',
            }}
            icon={['fass', 'newspaper']}
          />
          {canEditSailingInfo(user) && (
            <BubbleButton
              className="p-2-i ml-2"
              variant="slate"
              inverse
              onClick={() => {
                setDayToEdit({
                  sailing: item._data.sailing,
                  day: item._data.day,
                  dayIndex: item._data.dayIndex,
                });
              }}
              icon="edit"
            />
          )}
        </>
      ),
      tdProps: makeDefaultTemplateTdProps,
    },
  ];

  function makeDefaultTemplateTdProps(
    data: ItineraryManagementTableItem,
    fieldTdProps: React.TdHTMLAttributes<HTMLTableCellElement>
  ) {
    const { isCurrentDay } = data;

    return {
      ...fieldTdProps,
      className: [
        fieldTdProps && fieldTdProps.className ? fieldTdProps.className : undefined,
        isCurrentDay ? 'bg-selected' : undefined,
      ]
        .filter(notUndefined)
        .join(' '),
      style: {},
    };
  }

  function sailingsConsistencyFix(sailing: BareSailing, type: PropertyType) {
    sailing.days.forEach((d, i) => {
      d.name =
        type === 'shore'
          ? d.date.toLocaleDateString('en', { weekday: 'long', timeZone: 'UTC' })
          : `${i + 1}`;
      d.voyage = sailing.name;
    });
  }

  function isJoinEnabled(item: ItineraryManagementSailingBundle, direction: -1 | 1) {
    const thisSailing = simplSailBndlBySail[item.sailingIndex];
    const otherSailing = simplSailBndlBySail[item.sailingIndex + direction];

    if (!thisSailing || !otherSailing) return false;

    return direction === -1
      ? lastEl(otherSailing).dateIso === firstEl(thisSailing).dateIso
      : firstEl(otherSailing).dateIso === lastEl(thisSailing).dateIso;
  }

  function isPlusEnabled(item: ItineraryManagementSailingBundle, direction: -1 | 1) {
    const thisSailing = simplSailBndlBySail[item.sailingIndex];
    const otherSailing = simplSailBndlBySail[item.sailingIndex + direction];

    if (!thisSailing) return false;

    const canJoin = isJoinEnabled(item, direction);

    if (canJoin) {
      // jointed checks (e.g. when there are no gaps)
      if (!otherSailing) return false;
      if (otherSailing.length <= 2) return false;
    } else {
      // disjointed checks (when there are gaps)
      if (
        otherSailing &&
        direction === 1 &&
        firstEl(otherSailing).dateIso < lastEl(thisSailing).dateIso
      ) {
        return false;
      }

      if (
        otherSailing &&
        direction === -1 &&
        lastEl(otherSailing).dateIso > firstEl(thisSailing).dateIso
      ) {
        return false;
      }
    }

    return true;
  }

  function isMinusEnabled(item: ItineraryManagementSailingBundle, direction: -1 | 1) {
    const thisSailing = simplSailBndlBySail[item.sailingIndex];
    const otherSailing = simplSailBndlBySail[item.sailingIndex + direction];

    if (!thisSailing) return false;

    const canJoin = isJoinEnabled(item, direction);

    if (canJoin) {
      // jointed checks (e.g. when there are no gaps)
      if (!otherSailing) return false;
      if (thisSailing.length <= 2) return false;
    } else {
      // disjointed checks (when there are gaps)
      if (
        otherSailing &&
        direction === 1 &&
        firstEl(otherSailing).dateIso > lastEl(thisSailing).dateIso
      ) {
        return false;
      }

      if (
        otherSailing &&
        direction === -1 &&
        lastEl(otherSailing).dateIso < firstEl(thisSailing).dateIso
      ) {
        return false;
      }
    }

    return true;
  }

  async function handleSimplSailMoveWithinSail(
    item: ItineraryManagementSailingBundle,
    direction: -1 | 1
  ) {
    const copySailing = { ...simpleSailings[item.sailingIndex] };

    // move the days within the sailing
    move(copySailing.days, item.dayIndex, item.dayIndex + direction);

    const currentDay = copySailing.days[item.dayIndex];
    const nextDay = copySailing.days[item.dayIndex + direction];

    // swap dates and names of the days back (we don't want those shifted)
    swap(currentDay, nextDay, 'date');
    swap(currentDay, nextDay, 'name');

    try {
      setLoadingItineraries(true);
      // since we are working with simple sailings we do not have feeds, but we need to get those
      // feeds since we are shifting days around the itinerary (and we want those feeds to stay)
      // hence we make a dictionary of days of the sailings so that we can grab feeds as needed.
      const daysInDbDict = mapBy((await getSailing(copySailing._id)).days, '_id');

      const sailing = unsimplifyBareSailing(
        copySailing,
        copySailing.days.map(d => daysInDbDict[d._id].feeds)
      );

      await updateSailing(sailing._id, sailing);
    } finally {
      setLoadingItineraries(false);
      fetchItineraries().catch(console.error);
    }
  }

  async function handleSimplSailMoveOverSail(
    item: ItineraryManagementSailingBundle,
    direction: -1 | 1,
    isTaking: boolean
  ) {
    const copyThisSailing = { ...simpleSailings[item.sailingIndex] };
    const copyOtherSailing = { ...simpleSailings[item.sailingIndex + direction] };

    if (isTaking) {
      // we will take a day from the other sailing
      const thisDay =
        direction === -1 ? copyOtherSailing.days.pop() : copyOtherSailing.days.splice(0, 1)[0];
      const otherDay =
        direction === -1 ? lastEl(copyOtherSailing.days) : firstEl(copyOtherSailing.days);

      if (!thisDay || !otherDay) return;

      thisDay.date = otherDay.date;

      if (direction === -1) copyThisSailing.days.splice(0, 0, thisDay);
      else copyThisSailing.days.push(thisDay);
    } else {
      // we wil donate a day to the other sailing
      const thisDay =
        direction === -1 ? copyThisSailing.days.splice(0, 1)[0] : copyThisSailing.days.pop();
      const otherDay =
        direction === -1 ? firstEl(copyThisSailing.days) : lastEl(copyThisSailing.days);

      if (!thisDay || !otherDay) return;

      thisDay.date = otherDay.date;

      if (direction === -1) copyOtherSailing.days.push(thisDay);
      else copyOtherSailing.days.splice(0, 0, thisDay);
    }

    try {
      setLoadingItineraries(true);
      // since we are working with simple sailings we do not have feeds, but we need to get those
      // feeds since we are shifting days around the itinerary (and we want those feeds to stay)
      // hence we make a dictionary of days of the sailings so that we can grab feeds as needed.
      const daysInDbDict = mapBy(
        (await Promise.all([getSailing(copyThisSailing._id), getSailing(copyOtherSailing._id)]))
          .map(s => s.days)
          .flat(),
        '_id'
      );

      const thisSailing = unsimplifyBareSailing(
        copyThisSailing,
        copyThisSailing.days.map(d => daysInDbDict[d._id].feeds)
      );
      const otherSailing = unsimplifyBareSailing(
        copyOtherSailing,
        copyOtherSailing.days.map(d => daysInDbDict[d._id].feeds)
      );

      sailingsConsistencyFix(thisSailing, activeProperty.type);
      sailingsConsistencyFix(otherSailing, activeProperty.type);

      await Promise.all([
        updateSailing(thisSailing._id, thisSailing),
        updateSailing(otherSailing._id, otherSailing),
      ]);
    } finally {
      setLoadingItineraries(false);
      fetchItineraries().catch(console.error);
    }
  }

  async function handleSimplSailAddDay(
    item: ItineraryManagementSailingBundle,
    direction: -1 | 1,
    amt: number = 1
  ) {
    const copySailing = { ...simpleSailings[item.sailingIndex] };
    const copyDay =
      direction === -1 ? { ...firstEl(copySailing.days) } : { ...lastEl(copySailing.days) };

    const newDays: Array<Omit<NewBareSailingDay, 'plan' | 'feeds' | 'activeFeed'>> = [];
    let nextDate = new Date(copyDay.date);

    for (let i = 0; i < amt; i++) {
      nextDate = addDaysToDateUTC(nextDate, direction);

      newDays.push({
        name: '',
        date: nextDate,
        type: 'port',
        locations: [],
        voyage: copyDay.voyage,
        saved: false,
        locked: false,
      });
    }

    try {
      setLoadingItineraries(true);
      // since we are working with simple sailings we do not have feeds, but we need to get those
      // feeds since we are shifting days around the itinerary (and we want those feeds to stay)
      // hence we make a dictionary of days of the sailings so that we can grab feeds as needed.
      const daysInDbDict = mapBy((await getSailing(copySailing._id)).days, '_id');

      const sailing = unsimplifyBareSailing(
        copySailing,
        copySailing.days.map(d => daysInDbDict[d._id].feeds)
      );

      if (direction === 1) {
        // simple case, just add and save
        sailing.days = sailing.days.concat(newDays as BareSailingDay[]);

        sailingsConsistencyFix(sailing, activeProperty.type);

        await updateSailing(sailing._id, sailing);
      } else {
        // FIXME: is this really necessary?
        // complex case, still needs to be last to be 'added', but then need to
        // move to top because that is where the user intends to save it
        sailing.days = sailing.days.concat(newDays as BareSailingDay[]);

        sailingsConsistencyFix(sailing, activeProperty.type);

        const updatedSailing = await updateSailing(sailing._id, sailing);

        const swappingDays = updatedSailing.days.splice(-1, newDays.length);
        if (!swappingDays.length) return;
        updatedSailing.days.unshift(...swappingDays);

        sailingsConsistencyFix(updatedSailing, activeProperty.type);

        await updateSailing(updatedSailing._id, updatedSailing);
      }
    } finally {
      setLoadingItineraries(false);
      fetchItineraries().catch(console.error);
    }
  }

  async function handleSimplSailRemoveDay(
    item: ItineraryManagementSailingBundle,
    direction: -1 | 1
  ) {
    const decision = await Alert.question(
      'This is a destructive operation that will delete the day, removing the daily planner and feeds associated with it. Are you sure you want to do this?'
    );
    if (!decision) return;

    const copySailing = { ...simpleSailings[item.sailingIndex] };

    if (direction === 1) copySailing.days.pop();
    else copySailing.days.splice(0, 1);

    try {
      setLoadingItineraries(true);
      // since we are working with simple sailings we do not have feeds, but we need to get those
      // feeds since we are shifting days around the itinerary (and we want those feeds to stay)
      // hence we make a dictionary of days of the sailings so that we can grab feeds as needed.
      const daysInDbDict = mapBy((await getSailing(copySailing._id)).days, '_id');

      const sailing = unsimplifyBareSailing(
        copySailing,
        copySailing.days.map(d => daysInDbDict[d._id].feeds)
      );

      sailingsConsistencyFix(sailing, activeProperty.type);

      await updateSailing(sailing._id, sailing);
    } finally {
      setLoadingItineraries(false);
      fetchItineraries().catch(console.error);
    }
  }

  async function handlePlusDay(item: ItineraryManagementSailingBundle, direction: -1 | 1) {
    const canJoin = isJoinEnabled(item, direction);
    if (canJoin) {
      await handleSimplSailMoveOverSail(item, direction, true);
    } else {
      await handleSimplSailAddDay(item, direction);
    }
  }

  async function handleMinusDay(item: ItineraryManagementSailingBundle, direction: -1 | 1) {
    const canJoin = isJoinEnabled(item, direction);
    if (canJoin) {
      await handleSimplSailMoveOverSail(item, direction, false);
    } else {
      await handleSimplSailRemoveDay(item, direction);
    }
  }

  async function handleCrossDay(item: ItineraryManagementSailingBundle) {
    await handleSimplSailRemoveDay(item, 1);
  }

  async function handleSimplSailSplitSail(splitPoint: ItineraryManagementSailingBundle) {
    const copyThisSailing = { ...simpleSailings[splitPoint.sailingIndex] };
    const copyThisDay = { ...copyThisSailing.days[splitPoint.dayIndex] };

    const firstHalfOfDays = copyThisSailing.days.slice(0, splitPoint.dayIndex);
    const secondHalfOfDays = copyThisSailing.days.slice(
      splitPoint.dayIndex,
      copyThisSailing.days.length
    );

    const thisSimpleSailing = { ...copyThisSailing, days: firstHalfOfDays };
    const otherSimpleSailing = { ...copyThisSailing, days: secondHalfOfDays };

    try {
      setLoadingItineraries(true);
      // since we are working with simple sailings we do not have feeds, but we need to get those
      // feeds since we are shifting days around the itinerary (and we want those feeds to stay)
      // hence we make a dictionary of days of the sailings so that we can grab feeds as needed.
      const daysInDbDict = mapBy((await getSailing(copyThisSailing._id)).days, '_id');

      const thisSailing = unsimplifyBareSailing(
        thisSimpleSailing,
        thisSimpleSailing.days.map(d => daysInDbDict[d._id].feeds)
      );
      const otherSailing = unsimplifyBareSailing(
        otherSimpleSailing,
        otherSimpleSailing.days.map(d => daysInDbDict[d._id].feeds)
      );

      sailingsConsistencyFix(thisSailing, activeProperty.type);
      sailingsConsistencyFix(otherSailing, activeProperty.type);

      await Promise.all([updateSailing(thisSailing._id, thisSailing), createSailing(otherSailing)]);

      // add an extra bleed day to current sailing
      const newDay: Omit<NewBareSailingDay, 'plan' | 'feeds' | 'activeFeed'> = {
        name: '',
        date: copyThisDay.date,
        type: copyThisDay.type,
        locations: copyThisDay.locations,
        voyage: copyThisDay.voyage,
        saved: copyThisDay.saved,
        locked: copyThisDay.locked,
      };

      thisSailing.days.push(newDay as BareSailingDay);

      sailingsConsistencyFix(thisSailing, activeProperty.type);

      await updateSailing(thisSailing._id, thisSailing);
    } finally {
      setLoadingItineraries(false);
      fetchItineraries().catch(console.error);
    }
  }

  async function handleSimplSailJoinSail(
    joinPoint: ItineraryManagementSailingBundle,
    direction: -1 | 1
  ) {
    const decision = await Alert.question(
      `This is a destructive operation that will delete the "bleed" day of the ${
        direction === -1 ? 'previous' : 'next'
      } sailing, removing the daily planner and feeds associated with it. Are you sure you want to do this?`
    );
    if (!decision) return;

    const copyThisSailing = { ...simpleSailings[joinPoint.sailingIndex] };
    const copyOtherSailing = { ...simpleSailings[joinPoint.sailingIndex + direction] };

    try {
      setLoadingItineraries(true);
      // since we are working with simple sailings we do not have feeds, but we need to get those
      // feeds since we are shifting days around the itinerary (and we want those feeds to stay)
      // hence we make a dictionary of days of the sailings so that we can grab feeds as needed.
      const daysInDbDict = mapBy(
        (await Promise.all([getSailing(copyThisSailing._id), getSailing(copyOtherSailing._id)]))
          .map(s => s.days)
          .flat(),
        '_id'
      );

      const thisSailing = unsimplifyBareSailing(
        copyThisSailing,
        copyThisSailing.days.map(d => daysInDbDict[d._id].feeds)
      );
      const otherSailing = unsimplifyBareSailing(
        copyOtherSailing,
        copyOtherSailing.days.map(d => daysInDbDict[d._id].feeds)
      );

      // assuming:
      // A: [1,2,3]
      // B: [3',4,5]
      // C: [5',6,7]
      // and 'current' (selected) sailing is B:
      if (direction === -1) {
        // user intends to consume all (except bleed) of 'other' (previous) sailing (A), such that:
        // A: [3]
        // B: [1,2,3',4,5]
        // C: [5',6,7]
        const daysToMove = otherSailing.days.splice(0, otherSailing.days.length - 1);
        thisSailing.days.unshift(...daysToMove);
      } else {
        // user intends to consume all (except bleed) of 'other' (next) sailing (C), such that:
        // A: [1,2,3]
        // B: [3',4,5,6,7]
        // C: [5']
        const daysToMove = otherSailing.days.splice(1, otherSailing.days.length);
        thisSailing.days.push(...daysToMove);
      }

      sailingsConsistencyFix(thisSailing, activeProperty.type);
      sailingsConsistencyFix(otherSailing, activeProperty.type);

      await Promise.all([
        updateSailing(thisSailing._id, thisSailing),
        updateSailing(otherSailing._id, otherSailing),
      ]);
      await deleteSailing(otherSailing._id);
    } finally {
      setLoadingItineraries(false);
      fetchItineraries().catch(console.error);
    }
  }

  async function handleSubmitSailingChanges(sailingEditForm: EditSailingForm) {
    if (!sailingToEdit) return;

    const newSimpleSailing = { ...sailingToEdit, name: sailingEditForm.name };

    try {
      setLoadingItineraries(true);
      // since we are working with simple sailings we do not have feeds, but we need to get those
      // feeds since we are shifting days around the itinerary (and we want those feeds to stay)
      // hence we make a dictionary of days of the sailings so that we can grab feeds as needed.
      const daysInDbDict = mapBy((await getSailing(newSimpleSailing._id)).days, '_id');

      const newSailing = unsimplifyBareSailing(
        newSimpleSailing,
        newSimpleSailing.days.map(d => daysInDbDict[d._id].feeds)
      );

      await updateSailing(newSailing._id, newSailing);
    } finally {
      setLoadingItineraries(false);
      fetchItineraries().catch(console.error);
    }
  }

  async function handleDeleteSailing() {
    if (!sailingToEdit) return;

    const question = await Alert.question(
      'Are you certain you wish to delete this sailing? Please be aware that this action is permanent and cannot be undone.'
    );
    if (!question) return;

    try {
      setLoadingItineraries(true);
      await deleteSailing(sailingToEdit._id);
    } finally {
      setLoadingItineraries(false);
      fetchItineraries().catch(console.error);
    }
  }

  async function handleSubmitDayChanges(dayEditForm: EditDayForm) {
    if (!dayToEdit) return;

    const newSimpleSailing = { ...dayToEdit.sailing };
    newSimpleSailing.days.splice(dayToEdit.dayIndex, 1, {
      ...dayToEdit.day,
      type: dayEditForm.type,
      locations: dayEditForm.locations.filter(l => l.name.length),
      dayTheme: dayEditForm.dayTheme,
    });

    try {
      setLoadingItineraries(true);
      // since we are working with simple sailings we do not have feeds, but we need to get those
      // feeds since we are shifting days around the itinerary (and we want those feeds to stay)
      // hence we make a dictionary of days of the sailings so that we can grab feeds as needed.
      const daysInDbDict = mapBy((await getSailing(newSimpleSailing._id)).days, '_id');

      const newSailing = unsimplifyBareSailing(
        newSimpleSailing,
        newSimpleSailing.days.map(d => daysInDbDict[d._id].feeds)
      );

      await updateSailing(newSailing._id, newSailing);
    } finally {
      setLoadingItineraries(false);
      fetchItineraries().catch(console.error);
    }
  }

  async function handleSpotCreateSailing(type: PropertyType) {
    if (!spotCreateForm) return;

    const sailingPromises = [];

    for (let i = 0; i < spotCreateForm.repeatFor; i++) {
      const date = new Date(spotCreateForm.date);
      const newDays: Array<Omit<NewBareSailingDay, 'plan' | 'feeds' | 'activeFeed'>> = [];

      let nextDate = addDaysToDateUTC(date, spotCreateForm.repeatEvery * i);

      let sailingName = 'New Sailing';

      if (spotCreateForm.useDateSailingNames) {
        sailingName = `${getLocaleDateStringUTC(nextDate, undefined, {
          day: '2-digit',
          month: 'long',
        })}`;
      }

      const locations = spotCreateForm.locations.split(',');

      for (let j = 0; j < spotCreateForm.numOfDays; j++) {
        const locInfo = locations[j % locations.length];

        newDays.push({
          name: spotCreateForm.useDateSailingNames
            ? `${getLocaleDateStringUTC(nextDate, undefined, { day: '2-digit', weekday: 'long' })}`
            : `${j + 1}`,
          date: nextDate,
          type: type === 'sea' && locInfo === '' ? 'sea' : 'port',
          locations: !locInfo ? [] : [{ name: locInfo }],
          voyage: sailingName,
          saved: false,
          locked: false,
        });
        nextDate = addDaysToDateUTC(nextDate, 1);
      }

      const newSailing: BareSailing = {
        _id: '',
        name: sailingName,
        property: activeProperty._id,
        days: newDays as BareSailingDay[],
      };

      sailingsConsistencyFix(newSailing, activeProperty.type);

      sailingPromises.push(createSailing(newSailing));
    }

    try {
      setLoadingItineraries(true);
      await Promise.all(sailingPromises);
      setSpotCreateForm(undefined);
    } finally {
      setLoadingItineraries(false);
      fetchItineraries().catch(console.error);
    }
  }

  return (
    <PageWrapper {...props}>
      <Page className="ItineraryManagement absolute flex items-stretch">
        <MenuBar {...props} property={activeProperty} />
        <div className="SideMenu bg-navy-darkest flex flex-col">
          <div className="bg-white flex flex-col items-center">
            <BrandLogo logo={activeProperty.brand.logo} />
            <PropertyBanner {...props} property={activeProperty} swappable />
          </div>
          <div className="relative flex flex-col items-stretch p-4 bg-navy-darker">
            <div className="grid gap-2">
              <div className="flex items-stretch rounded-lg overflow-hidden bg-navy border border-navy-darkest">
                <div className="flex-grow px-4 py-2">
                  <BubbleDebounce onTrigger={[(date: string) => setCurrentDate(date), 500]}>
                    {({ debounce }) => (
                      <BubbleInput
                        type="date"
                        block
                        appearance="bare"
                        inputClassName="bg-navy text-white inverse-indicator"
                        value={currentDate}
                        onValue={debounce}
                      />
                    )}
                  </BubbleDebounce>
                </div>
                {currentDate !== DEFAULT_SAILING_DATE && (
                  <BubbleButton
                    variant="navy"
                    appearance="squared"
                    className="-ml-4 pl-2-i"
                    onClick={() => setCurrentDate(DEFAULT_SAILING_DATE)}
                    icon="rotate-left"
                  />
                )}
              </div>
              {user.superAdmin && (
                <BubbleButton
                  variant="lime"
                  onClick={() => {
                    setSpotCreateForm(generateSpotCreateForm(currentDate, activeProperty.type));
                  }}
                  className="text-left"
                  icon="plus"
                >
                  Spot Sailing
                </BubbleButton>
              )}
            </div>
          </div>
          <BubbleAccordion
            variant="navy"
            className="h-full flex flex-col border-t border-navy-darkest"
            labels={['Manage']}
            singleMode
            open={[0]}
          >
            <div className="grid p-4 gap-4">
              <BubbleButton
                variant="slate"
                linkProps={{ to: resolveItinerariesPath(activeProperty._id) }}
                icon="calendar-lines"
              >
                View Itineraries
              </BubbleButton>
            </div>
          </BubbleAccordion>
        </div>
        <div className="ItineraryManagementContent bg-blue-lightest overflow-y-auto flex-grow">
          <div
            className="ItineraryManagementHeader align-with-logo flex items-end px-5 sticky top-0
                left-0 z-10 bg-blue-lightest"
            style={{ zIndex: 12 }}
          >
            <div className="mb-3 pl-3 flex-grow flex items-end">
              <ItineraryNavigation active="itinerary" />
            </div>
          </div>
          <div className="ItineraryManagementBody mx-5 content-minus-logo min-w-900">
            <BubbleOverlay loading={loadingItineraries} hideBg loadingMinHeight="medium">
              {bulkTableItems.map((bti, idx) => (
                <BubbleTable
                  key={idx}
                  className="mb-3"
                  responsive
                  striped
                  headVariant="navy"
                  onRowClick={item => {
                    canEditSailingInfo(user) &&
                      setDayToEdit({
                        sailing: item._data.sailing,
                        day: item._data.day,
                        dayIndex: item._data.dayIndex,
                      });
                  }}
                  fields={tableFields}
                  items={bti.items}
                  keyExtractor={item => item.id}
                  stickyHeader={130}
                  templates={tableTemplates}
                  showPretitle
                  pretitleClassName="p-0-i"
                  pretitleRender={() => (
                    <BulkItineraryPretitle
                      tableItem={bti}
                      onSelectSailing={setSailingToEdit}
                      sailingToEdit={sailingToEdit}
                      onSubmitSailing={handleSubmitSailingChanges}
                      onDeleteSailing={handleDeleteSailing}
                    />
                  )}
                />
              ))}
              {!bulkTableItems.length && !loadingItineraries && (
                <BubbleAlert variant="banana">No itineraries to show</BubbleAlert>
              )}
            </BubbleOverlay>
            <div className="mt-3">&nbsp;</div>
          </div>
        </div>
        {dayToEdit && (
          <EditDayForm
            dayToEdit={dayToEdit}
            onClose={() => setDayToEdit(undefined)}
            onSubmit={handleSubmitDayChanges}
            onEditCanvas={() => {
              props.history.push(
                resolvePlannerPath(activeProperty._id, dayToEdit.sailing._id, dayToEdit.day._id)
              );
            }}
            onEditFeed={() => {
              props.history.push(
                resolveFeedPath(activeProperty._id, dayToEdit.sailing._id, dayToEdit.day._id)
              );
            }}
          />
        )}
        {spotCreateForm && (
          <Popup title="Spot Create Sailing" onClose={() => setSpotCreateForm(undefined)}>
            <hr />
            <div className="p-4" style={{ width: '500px' }}>
              <div className="grid grid-cols-1 gap-4">
                <div className="p-3 bg-eggshell shadow rounded border">
                  Use this tool to rapidly generate sailings. You specify the start date of the
                  sailing, the duration in days, and the repetition pattern (e.g., every 7 days, 52
                  times) which can start on specific dates like the first Sunday of the year. Please
                  exercise caution when using this tool, as it doesn't check for conflicts with
                  existing sailings.
                </div>
                <BubbleFormGroup label="Date">
                  <BubbleInput
                    type="date"
                    block
                    value={spotCreateForm.date}
                    onValue={val => setSpotCreateForm({ ...spotCreateForm, date: val })}
                  />
                </BubbleFormGroup>
                <BubbleFormGroup label="Days">
                  <BubbleInput
                    type="number"
                    min={1}
                    block
                    value={spotCreateForm.numOfDays}
                    onValue={val => setSpotCreateForm({ ...spotCreateForm, numOfDays: val })}
                  />
                </BubbleFormGroup>
                <BubbleFormGroup label="Repeat Every (# Days)">
                  <BubbleInput
                    type="number"
                    min={1}
                    block
                    value={spotCreateForm.repeatEvery}
                    onValue={val => setSpotCreateForm({ ...spotCreateForm, repeatEvery: val })}
                  />
                  <div className="italic text-slate mt-1">
                    Should be equal to days number, unless embark/debark is needed.
                  </div>
                </BubbleFormGroup>
                <BubbleFormGroup label="Repeat Amount">
                  <BubbleInput
                    type="number"
                    min={1}
                    block
                    value={spotCreateForm.repeatFor}
                    onValue={val => setSpotCreateForm({ ...spotCreateForm, repeatFor: val })}
                  />
                </BubbleFormGroup>
                <BubbleFormGroup label="Locations">
                  <BubbleInput
                    type="text"
                    min={1}
                    block
                    value={spotCreateForm.locations}
                    onValue={val => setSpotCreateForm({ ...spotCreateForm, locations: val })}
                  />
                  <div className="italic text-slate mt-1">
                    Comma seperated list of locations, one for each day. Leave empty for{' '}
                    {activeProperty.type === 'sea' ? 'At Sea' : 'no location'} for that day.
                  </div>
                </BubbleFormGroup>
                <div className="flex items-center">
                  <BubbleInput
                    type="checkbox"
                    block
                    checked={spotCreateForm.useDateSailingNames}
                    onValue={chk =>
                      setSpotCreateForm({ ...spotCreateForm, useDateSailingNames: chk })
                    }
                  />
                  <span className="ml-1">Use month/week number as sailing names</span>
                </div>
              </div>
              <BubbleButton
                variant="navy"
                className="mt-4"
                onClick={() => handleSpotCreateSailing(activeProperty.type)}
              >
                Spot Create Sailing
              </BubbleButton>
            </div>
          </Popup>
        )}
      </Page>
    </PageWrapper>
  );
};

export default ItineraryManagement;

interface EditSailingFormProps {
  tableItem: BulkItineraryManagementTableItem;
  sailingToEdit: BareSimpleSailing | undefined;
  onSelectSailing: (sailing: BareSimpleSailing | undefined) => void;
  onSubmitSailing: (form: EditSailingForm) => void;
  onDeleteSailing: () => void;
}

function BulkItineraryPretitle(props: EditSailingFormProps) {
  const [sailingEditForm, setSailingEditForm] =
    useState<EditSailingForm>(generateSailingEditForm());

  useEffect(() => {
    setSailingEditForm(generateSailingEditForm(props.sailingToEdit));
  }, [props.sailingToEdit]);

  function changeSailingEditForm<T extends keyof EditSailingForm>(key: T, val: EditSailingForm[T]) {
    setSailingEditForm({ ...sailingEditForm, _dirty: true, [key]: val });
  }

  const isSelected = props.tableItem.sailing._id === props.sailingToEdit?._id;

  return (
    <div
      className="py-1"
      style={{ backgroundColor: hashStringToColor(props.tableItem.sailing.name) }}
    >
      <BubbleButton
        variant="white"
        inverse
        className="text-center"
        block
        onClick={() => props.onSelectSailing(isSelected ? undefined : props.tableItem.sailing)}
        iconSuffix={isSelected ? 'chevron-up' : 'chevron-down'}
      >
        {props.tableItem.sailing.name}
      </BubbleButton>
      {isSelected && (
        <div className="px-4 pb-4">
          <div
            className="bg-white p-4 text-black font-normal
          normal-case shadow-md rounded"
          >
            <div className="grid gap-4">
              <BubbleFormGroup label="Name">
                <BubbleInput
                  type="text"
                  block
                  value={sailingEditForm.name}
                  onValue={val => changeSailingEditForm('name', val)}
                />
              </BubbleFormGroup>
            </div>
            <div className="flex items-center">
              <BubbleButton
                variant="navy"
                className="mt-3"
                disabled={!sailingEditForm._dirty}
                onClick={() => props.onSubmitSailing(sailingEditForm)}
                icon="floppy-disk"
              >
                Save Changes
              </BubbleButton>
              <BubbleButton
                variant="crimson"
                inverse
                className="ml-auto"
                onClick={props.onDeleteSailing}
                icon="trash-alt"
              >
                Delete Sailing
              </BubbleButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
